import { available, notAvailable, type MetricResult } from "./types";

function assertFinite(values: readonly number[]): void {
  if (values.some((value) => !Number.isFinite(value))) {
    throw new Error("Decomposition requires finite common-currency/common-unit prices.");
  }
}
export interface HkDecomposition {
  readonly template: "H/K";
  readonly executableSpread: number;
  readonly matchedMaturityBasis: number;
  readonly iceCurveContribution: number;
  readonly identityError: number;
  readonly unit: "CAD/metric-tonne";
}

export function decomposeMatifHk(input: {
  readonly canolaH: number;
  readonly canolaK: number;
  readonly matifK: number;
}): HkDecomposition {
  assertFinite([input.canolaH, input.canolaK, input.matifK]);
  const executableSpread = input.matifK - input.canolaH;
  const matchedMaturityBasis = input.matifK - input.canolaK;
  const iceCurveContribution = input.canolaK - input.canolaH;
  return {
    template: "H/K",
    executableSpread,
    matchedMaturityBasis,
    iceCurveContribution,
    identityError: executableSpread - matchedMaturityBasis - iceCurveContribution,
    unit: "CAD/metric-tonne",
  };
}

export interface NqDecomposition {
  readonly template: "N/Q";
  readonly executableSpread: number;
  readonly xxCrossMarketBasis: number;
  readonly iceCurveContribution: number;
  readonly matifCurveContribution: number;
  readonly identityError: number;
  readonly unit: "CAD/metric-tonne";
  readonly cropBoundaryWarning: true;
}

export function decomposeMatifNq(input: {
  readonly canolaN: number;
  readonly canolaX: number;
  readonly matifQ: number;
  readonly matifX: number;
}): NqDecomposition {
  assertFinite([input.canolaN, input.canolaX, input.matifQ, input.matifX]);
  const executableSpread = input.matifQ - input.canolaN;
  const xxCrossMarketBasis = input.matifX - input.canolaX;
  const iceCurveContribution = input.canolaX - input.canolaN;
  const matifCurveContribution = input.matifQ - input.matifX;
  return {
    template: "N/Q",
    executableSpread,
    xxCrossMarketBasis,
    iceCurveContribution,
    matifCurveContribution,
    identityError:
      executableSpread - xxCrossMarketBasis - iceCurveContribution - matifCurveContribution,
    unit: "CAD/metric-tonne",
    cropBoundaryWarning: true,
  };
}

export interface MatchedMaturityDecomposition {
  readonly template: "K/K" | "X/X";
  readonly executableSpread: number;
  readonly matchedMaturityBasis: number;
  readonly iceCurveContribution: 0;
  readonly matifCurveContribution: 0;
  readonly unit: "CAD/metric-tonne";
}

export function decomposeMatchedMaturity(
  template: "K/K" | "X/X",
  canola: number,
  matif: number,
): MatchedMaturityDecomposition {
  assertFinite([canola, matif]);
  return {
    template,
    executableSpread: matif - canola,
    matchedMaturityBasis: matif - canola,
    iceCurveContribution: 0,
    matifCurveContribution: 0,
    unit: "CAD/metric-tonne",
  };
}

export type MatifDecomposition = HkDecomposition | NqDecomposition | MatchedMaturityDecomposition;

export type MatifTemplateInput =
  | { readonly template: "H/K"; readonly canolaH: number; readonly canolaK: number; readonly matifK: number }
  | { readonly template: "N/Q"; readonly canolaN: number; readonly canolaX: number; readonly matifQ: number; readonly matifX: number }
  | { readonly template: "K/K" | "X/X"; readonly canola: number; readonly matif: number }
  | { readonly template: "F/G" };

export function decomposeMatifPair(input: MatifTemplateInput): MetricResult<MatifDecomposition> {
  switch (input.template) {
    case "H/K":
      return available(decomposeMatifHk(input), 1, "K-anchor exact accounting identity");
    case "N/Q":
      return available(decomposeMatifNq(input), 1, "X-anchor exact accounting identity");
    case "K/K":
    case "X/X":
      return available(decomposeMatchedMaturity(input.template, input.canola, input.matif), 1, "matched-maturity basis");
    case "F/G":
      return notAvailable("unsupported-template", "F/G has no defensible mandatory anchor; supplemental decomposition is N/A.", 0);
  }
}

/** Exact midpoint attribution for a local MATIF price converted with EURCAD. */
export function attributeFxAdjustedPriceChange(input: {
  readonly previousLocalPrice: number;
  readonly currentLocalPrice: number;
  readonly previousFx: number;
  readonly currentFx: number;
}): {
  readonly totalCadChange: number;
  readonly localPriceContribution: number;
  readonly fxContribution: number;
  readonly identityError: number;
  readonly method: "midpoint-shapley";
} {
  assertFinite([
    input.previousLocalPrice,
    input.currentLocalPrice,
    input.previousFx,
    input.currentFx,
  ]);
  const totalCadChange = input.currentLocalPrice * input.currentFx - input.previousLocalPrice * input.previousFx;
  const localPriceContribution =
    (input.currentLocalPrice - input.previousLocalPrice) * (input.currentFx + input.previousFx) / 2;
  const fxContribution =
    (input.currentFx - input.previousFx) * (input.currentLocalPrice + input.previousLocalPrice) / 2;
  return {
    totalCadChange,
    localPriceContribution,
    fxContribution,
    identityError: totalCadChange - localPriceContribution - fxContribution,
    method: "midpoint-shapley",
  };
}
